import classNames from "classnames"
import { Button } from "../../ui/Button/Button"
import { Icon } from "../../ui/Icon/Icon"
import { Text } from "../../ui/Text/Text"
import { AddToCartButton } from "../AddToCartButton/AddToCartButton"
import { DispatchEstimate } from "../DispatchEstimate/DispatchEstimate"
import { useApp } from "../../../hooks/useApp"
import { getMainProductVariantById, getPendantProductVariantById, isPos, saveActivePendantsToCookies, getTotalPrice } from "../../../utils"
import { formatCentsToDollars, formatYGWG } from "../../../utils/formatCentsToDollars"

interface Props {
  className?: string
}

export const DesignSummaryPopup = ({ className }: Props) => {
  const { 
    mainProduct, 
    activeMainProductVariantId, 
    activePendantProductVaraintIds, 
    setActivePendantProductVaraintIds, 
    setActive, 
    designSummaryPopupOpen, 
    setDesignSummaryPopupOpen,
  } = useApp()

  const activeMainProductVariant = getMainProductVariantById(mainProduct, activeMainProductVariantId)

  const totalPrice = getTotalPrice(mainProduct, activeMainProductVariantId, activePendantProductVaraintIds)

  return (
    <>
      <div 
        className={classNames(
          "fixed z-50 left-0 top-0 right-0 bottom-0 bg-[#343434]",
          "transition-opacity duration-500",
          designSummaryPopupOpen 
            ? "opacity-50 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
        onClick={() => setDesignSummaryPopupOpen(false)}
      />

      <section className={classNames(
        "fixed left-[50%] xl:left-[75%] top-1/2 z-50 w-[calc(100vw-2.5rem)] xl:w-[calc(50vw-240px-1.25rem)] rounded-sm -translate-x-1/2 bg-[#FAFAF9]",
        "transform transition-all duration-500",
        designSummaryPopupOpen 
          ? "-translate-y-1/2 opacity-100 pointer-events-auto"
          : "translate-y-[calc(100%+50px)] opacity-0 pointer-events-none",
        className
      )}>
        <div className={classNames(
          "transform transition-all duration-500 delay-300",
          "grid grid-rows-[min-content_1fr_min-content] max-h-[calc(100vh-200px)]",
          designSummaryPopupOpen 
            ? "pointer-events-auto"
            : "translate-y-[50px] opacity-0 pointer-events-none",
        )}>
          <header className="relative p-5 py-7">
            <p className="text-center">
              <Text style="h6" className="order-[-1] capitalize">Personalisation Studio</Text>
            </p>

            <Button className="absolute top-[50%] translate-y-[-50%]" size="sm" color="gray" onClick={() => setDesignSummaryPopupOpen(false)}>
              {/* @TODO: Fix icon later */}
              <Icon className="rotate-180" type="chevron-down" />
            </Button>
          </header>

          <div className="flex flex-col items-center px-5 overflow-y-auto md:px-12">
            <h3>
              <Text style="h4">Design Summary</Text>
            </h3>

            <ul className="w-full mt-8 border-b border-black">
              <li className="grid grid-cols-2 p-4 border-b border-subdued">
                <h4>
                  <Text style="h6" className="capitalize">{mainProduct.title}</Text>
                </h4>
                <p className="flex flex-col items-end gap-2">
                  <Text style="label">{formatYGWG(activeMainProductVariant.title)}</Text>
                  <Text style="label">{formatCentsToDollars(activeMainProductVariant.price)}</Text>
                </p>
              </li>

              {activePendantProductVaraintIds.map((activePendantProductVaraintId, index) => {
                // @TODO: Move to utilities
                const pendantProductVariant = getPendantProductVariantById(mainProduct, activePendantProductVaraintId)

                const handlePendantRemove = () => {
                  const nextActivePendantProductVaraintIds = [
                    ...activePendantProductVaraintIds.slice(0, index), 
                    ...activePendantProductVaraintIds.slice(
                      index + 1, 
                      activePendantProductVaraintIds.length
                    )
                  ]
                  setActivePendantProductVaraintIds(nextActivePendantProductVaraintIds)
                  setActive(0)
                }

                return (
                  <li className="grid grid-cols-2 p-4 border-b border-subdued" key={index}>
                    <h4>
                      <Text style="h6" className="capitalize">{formatYGWG(pendantProductVariant.title)}</Text>
                    </h4>
                    <p className="flex flex-col items-end gap-2">
                      <Text style="label">{formatCentsToDollars(pendantProductVariant.price)}</Text>
                      {activePendantProductVaraintIds.length > 2 && (
                        <Button onClick={handlePendantRemove}>
                          <Text style="caption" className="underline cursor-pointer hover:no-underline">Remove</Text>
                        </Button>
                      )}
                    </p>
                  </li>
                )
              })}
            </ul>

            <p className="flex justify-between items-center gap-5 w-full p-4">
              <Text style="h6" className="capitalize">Subtotal</Text>
              <Text style="label" className="capitalize">{formatCentsToDollars(totalPrice)}</Text>
            </p>
          </div>

          <footer className="flex flex-col items-center gap-5 p-5 py-7">
            <DispatchEstimate className="text-center" />
            <AddToCartButton className="w-full"/>
          </footer>
        </div>
      </section>
    </>
  )
}          
          